import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Plot } from '../../types';
import apiService from '../../services/api';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  MapIcon
} from '@heroicons/react/24/outline';

interface HistoryEntry {
  id: number;
  new_status_id: number;
  status_name: string;
  status_color: string;
  updated_by_name?: string;
  updated_at: string;
  notes?: string;
}

const PlotDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [plot, setPlot] = useState<Plot | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const fetchPlot = async () => {
      try {
        setLoading(true);
        const response = await apiService.getPlot(parseInt(id));
        setPlot(response.data.plot);
        setHistory(response.data.statusHistory || []);
      } catch (err: any) {
        setError(err.message || 'Failed to load plot details');
      } finally {
        setLoading(false);
      }
    };
    fetchPlot();
  }, [id]);

  const formatPrice = (price: number) => `₹${price.toLocaleString()}`;

  const badge = (name?: string, color?: string) => (
    <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: color + '20', color }}>
      {name}
    </span>
  );

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-40"></div>
        <div className="card p-6">
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Link to="/plots" className="btn-outline inline-flex items-center">
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back
        </Link>
        <div className="card p-6 text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => window.location.reload()} className="btn-primary">Retry</button>
        </div>
      </div>
    );
  }

  if (!plot) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Plot {plot.plot_number}</h1>
          <p className="text-gray-600">{plot.layout_name} &bull; {plot.layout_location}</p>
        </div>
        <Link to={`/plots?layout_id=${plot.layout_id}`} className="btn-outline inline-flex items-center">
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to Plots
        </Link>
      </div>

      {/* Plot Info */}
      <div className="card p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="font-semibold text-gray-700 mb-2">Details</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li><span className="font-medium text-gray-800">Status:</span> {badge(plot.status_name, plot.status_color)}</li>
            <li><span className="font-medium text-gray-800">Size:</span> {plot.size_sqft} sq ft ({plot.size_sqm} sq m)</li>
            <li><span className="font-medium text-gray-800">Price:</span> {formatPrice(plot.price)}</li>
            <li><span className="font-medium text-gray-800">Facing:</span> {plot.facing_direction || 'N/A'}</li>
            <li><span className="font-medium text-gray-800">Corner Plot:</span> {plot.corner_plot ? 'Yes' : 'No'}</li>
            {plot.notes && <li><span className="font-medium text-gray-800">Notes:</span> {plot.notes}</li>}
          </ul>
        </div>
        <div>
          <h3 className="font-semibold text-gray-700 mb-2">Customer</h3>
          {plot.customer_name ? (
            <ul className="text-sm text-gray-600 space-y-1">
              <li><span className="font-medium text-gray-800">Name:</span> {plot.customer_name}</li>
              <li><span className="font-medium text-gray-800">Phone:</span> {plot.customer_phone || '-'}</li>
              <li><span className="font-medium text-gray-800">Email:</span> {plot.customer_email || '-'}</li>
            </ul>
          ) : (
            <p className="text-gray-500">No customer assigned.</p>
          )}
        </div>
      </div>

      {/* Status History */}
      <div className="card p-6">
        <h3 className="font-semibold text-gray-700 mb-4">Recent Status History</h3>
        {history.length === 0 ? (
          <p className="text-gray-500">No history available.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Date</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Updated By</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {history.map((h) => (
                  <tr key={h.id}>
                    <td className="px-4 py-2 whitespace-nowrap">{new Date(h.updated_at).toLocaleString()}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{badge(h.status_name, h.status_color)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{h.updated_by_name || '-'}</td>
                    <td className="px-4 py-2 whitespace-pre-wrap max-w-xs">{h.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick link */}
      <Link to={`/layouts/${plot.layout_id}`} className="btn-primary inline-flex items-center">
        <MapIcon className="h-5 w-5 mr-1" /> View Layout Details
      </Link>
    </div>
  );
};

export default PlotDetailPage; 